import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../components/AppIcon";
import Button from "../components/ui/Button";
import { useAuth } from "../context/AuthContext";
import { getBusinessCredits, getBusinessOverview } from "../api/client";

// Business dashboard. The live data source is the credit wallet and the overview
// stats. Jobs + single-interview hiring are live (Phase 3); the multi-round
// pipeline step is also live (Phase 4).

function StatCard({ icon, label, value, hint, accent, onClick }) {
  const clickable = typeof onClick === "function";
  return (
    <div
      className={
        "rounded-xl border border-border bg-card p-5 " +
        (clickable ? "cursor-pointer transition-colors hover:bg-muted/40" : "")
      }
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span
          className={
            "flex h-8 w-8 items-center justify-center rounded-lg " +
            (accent || "bg-primary/10 text-primary")
          }
        >
          <Icon name={icon} size={16} />
        </span>
      </div>
      <div className="mt-3 text-2xl font-semibold text-foreground">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function ChecklistItem({ done, soon, title, description, actionLabel, onAction }) {
  return (
    <li className="flex items-start gap-3">
      <span
        className={
          "mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border " +
          (done
            ? "border-primary bg-primary text-primary-foreground"
            : "border-border bg-background text-transparent")
        }
      >
        <Icon name="Check" size={12} />
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">{title}</span>
          {soon && !done && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Soon
            </span>
          )}
        </div>
        <div className="text-sm text-muted-foreground">{description}</div>
        {!done && !soon && actionLabel && onAction && (
          <button
            onClick={onAction}
            className="mt-1.5 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            {actionLabel}
            <Icon name="ArrowRight" size={13} />
          </button>
        )}
      </div>
    </li>
  );
}

function OpsAction({ icon, title, description, actionLabel, onAction, tone = "primary" }) {
  const toneClass =
    tone === "warning"
      ? "bg-warning/15 text-warning"
      : tone === "success"
      ? "bg-success/15 text-success"
      : "bg-primary/10 text-primary";

  return (
    <button
      type="button"
      onClick={onAction}
      className="flex w-full items-start gap-3 rounded-lg border border-border bg-background p-4 text-left transition-colors hover:border-primary/40 hover:bg-muted/30"
    >
      <span className={"flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg " + toneClass}>
        <Icon name={icon} size={17} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-foreground">{title}</span>
        <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
          {description}
        </span>
        <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary">
          {actionLabel}
          <Icon name="ArrowRight" size={12} />
        </span>
      </span>
    </button>
  );
}

export default function Dashboard() {
  const { user, organization } = useAuth();
  const navigate = useNavigate();
  const firstName = (user?.name || "there").split(/\s+/)[0];

  const [wallet, setWallet] = useState(null);
  const [overview, setOverview] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [credits, ov] = await Promise.all([
          getBusinessCredits(),
          getBusinessOverview()
        ]);
        if (!alive) return;
        setWallet(credits);
        setOverview(ov);
      } catch {
        if (!alive) return;
        setWallet(null);
        setOverview(null);
      } finally {
        if (alive) setDataLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const walletBalance = wallet?.walletBalance ?? 0;
  const activeReservations = wallet?.activeReservations ?? 0;
  const lifetimePurchased = wallet?.lifetimePurchased ?? 0;
  const hasCredits = walletBalance > 0 || lifetimePurchased > 0;
  const activeJobs = overview?.activeJobs ?? 0;
  const draftJobs = overview?.draftJobs ?? 0;
  const closedJobs = overview?.closedJobs ?? 0;
  const candidatesActive = overview?.candidatesActive ?? 0;
  const finalists = overview?.finalists ?? 0;
  const hired = overview?.hired ?? 0;
  const awaitingReview = overview?.roundsAwaitingReview ?? 0;
  const awaitingReviewList = Array.isArray(overview?.awaitingReview)
    ? overview.awaitingReview
    : [];

  // Checklist derived completion flags
  const hasPostedJob = (activeJobs + draftJobs + closedJobs) > 0;
  const hasInvitedCandidates = candidatesActive > 0 || finalists > 0 || hired > 0;
  const hasRunPipeline = finalists > 0 || hired > 0 || awaitingReview > 0;
  const setupComplete = hasCredits && hasPostedJob && hasInvitedCandidates && hasRunPipeline;

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      {/* Greeting */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          Welcome, {firstName} 👋
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Here&apos;s an overview of hiring at {organization?.name || "your company"}.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          icon="Wallet"
          label="Wallet balance"
          value={!dataLoaded ? "…" : walletBalance}
          hint={hasCredits ? "Available to invite candidates" : "Buy credits to get started"}
          accent="bg-success/15 text-success"
          onClick={() => navigate("/business/credits")}
        />
        <StatCard
          icon="Briefcase"
          label="Active jobs"
          value={!dataLoaded ? "…" : activeJobs}
          hint="Jobs currently accepting candidates"
          accent="bg-blue-500/10 text-blue-500"
          onClick={() => navigate("/business/jobs")}
        />
        <StatCard
          icon="Users"
          label="Candidates in flight"
          value={!dataLoaded ? "…" : candidatesActive}
          hint="Active pipeline candidates"
          accent="bg-purple-500/10 text-purple-500"
          onClick={() => navigate("/business/jobs")}
        />
      </div>

      {/* Reviews Awaiting */}
      {awaitingReview > 0 && (
        <div className="rounded-xl border border-warning/30 bg-warning/10 p-4">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-warning/20 text-warning-foreground">
              <Icon name="AlertCircle" size={16} />
            </span>
            <div className="min-w-0">
              <h3 className="text-sm font-medium text-warning-foreground">
                Action required: {awaitingReview} {awaitingReview === 1 ? 'round is' : 'rounds are'} awaiting review
              </h3>
              <p className="text-xs text-warning-foreground/80">
                These pipeline rounds are paused for manual gate confirmation before candidates advance.
              </p>
            </div>
            {awaitingReviewList.length === 0 && (
              <div className="ml-auto flex-shrink-0">
                <Button size="sm" onClick={() => navigate("/business/jobs")}>
                  View Jobs
                </Button>
              </div>
            )}
          </div>
          {awaitingReviewList.length > 0 && (
            <ul className="mt-3 space-y-1.5 border-t border-warning/20 pt-3">
              {awaitingReviewList.map((r) => (
                <li key={r.roundId || `${r.jobId}-${r.roundNumber}`}>
                  <button
                    type="button"
                    onClick={() => navigate(`/business/jobs/${r.jobId}`)}
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-warning-foreground transition-colors hover:bg-warning/15"
                  >
                    <Icon name="Briefcase" size={14} className="flex-shrink-0 opacity-70" />
                    <span className="truncate font-medium">{r.jobTitle || "Untitled job"}</span>
                    <span className="truncate text-warning-foreground/70">
                      · Round {r.roundNumber}{r.roundName ? ` — ${r.roundName}` : ""}
                    </span>
                    <Icon name="ArrowRight" size={14} className="ml-auto flex-shrink-0 opacity-70" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Setup roadmap / operations */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-border bg-card p-6">
            {setupComplete ? (
              <>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Icon name="Activity" size={18} className="text-primary" />
                      <h2 className="text-base font-semibold text-foreground">Hiring cockpit</h2>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Your setup is complete. Keep your pipeline moving from here.
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-success/15 px-2.5 py-1 text-xs font-medium text-success">
                    <Icon name="CheckCircle2" size={13} />
                    Ready
                  </span>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-lg border border-border bg-background p-4">
                    <div className="text-2xl font-semibold tabular-nums text-foreground">
                      {finalists}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">Finalists</div>
                  </div>
                  <div className="rounded-lg border border-border bg-background p-4">
                    <div className="text-2xl font-semibold tabular-nums text-foreground">
                      {activeReservations}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">Reserved credits</div>
                  </div>
                  <div className="rounded-lg border border-border bg-background p-4">
                    <div className="text-2xl font-semibold tabular-nums text-foreground">
                      {awaitingReview}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">Rounds needing review</div>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <OpsAction
                    icon={awaitingReview > 0 ? "AlertCircle" : "Briefcase"}
                    title={awaitingReview > 0 ? "Review paused rounds" : "Review jobs"}
                    description={
                      awaitingReview > 0
                        ? "Confirm who advances from rounds waiting for your decision."
                        : "Open a job to inspect candidates, reports, and round status."
                    }
                    actionLabel={awaitingReview > 0 ? "Review now" : "Open jobs"}
                    tone={awaitingReview > 0 ? "warning" : "primary"}
                    onAction={() => navigate("/business/jobs")}
                  />
                  <OpsAction
                    icon="Library"
                    title="Improve question bank"
                    description="Add or import questions your recruiters can seed into future rounds."
                    actionLabel="Open question bank"
                    onAction={() => navigate("/business/question-bank")}
                  />
                  <OpsAction
                    icon="UserPlus"
                    title="Invite more candidates"
                    description="Add candidates to an active job and send their interview links."
                    actionLabel="Manage candidates"
                    onAction={() => navigate("/business/jobs")}
                  />
                  <OpsAction
                    icon="Wallet"
                    title="Manage credits"
                    description="Track available, reserved, and consumed interview credits."
                    actionLabel="Open credits"
                    tone="success"
                    onAction={() => navigate("/business/credits")}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Icon name="Rocket" size={18} className="text-primary" />
                  <h2 className="text-base font-semibold text-foreground">Get started</h2>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Here&apos;s how automated hiring comes together.
                </p>
                <ul className="mt-6 space-y-5">
                  <ChecklistItem
                    done
                    title="Create your business account"
                    description="You're all set — your hiring console is ready."
                  />
                  <ChecklistItem
                    done={hasCredits}
                    title="Add credits to your wallet"
                    description="Each candidate interview reserves one credit. Top up your wallet to start inviting."
                    actionLabel="Buy credits"
                    onAction={() => navigate("/business/buy-credits")}
                  />
                  <ChecklistItem
                    done={hasPostedJob}
                    title="Post a job"
                    description="Define a role and the interview rounds candidates will go through."
                    actionLabel={hasPostedJob ? "View jobs" : "Go to Jobs"}
                    onAction={() => navigate("/business/jobs")}
                  />
                  <ChecklistItem
                    done={hasInvitedCandidates}
                    title="Invite candidates by email"
                    description="Open a job and send unique interview links — candidates never see scores, only your team does."
                    actionLabel="View jobs"
                    onAction={() => navigate("/business/jobs")}
                  />
                  <ChecklistItem
                    done={hasRunPipeline}
                    title="Run a hiring pipeline"
                    description="Chain rounds with automatic advancement rules and let AI-scored results surface your strongest hires."
                  />
                </ul>
              </>
            )}
          </div>
        </div>

        {/* Account summary */}
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-2">
            <Icon name="Building2" size={18} className="text-primary" />
            <h2 className="text-base font-semibold text-foreground">Your company</h2>
          </div>
          <dl className="mt-5 space-y-4 text-sm">
            <div>
              <dt className="text-muted-foreground">Name</dt>
              <dd className="mt-0.5 font-medium text-foreground">
                {organization?.name || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Your role</dt>
              <dd className="mt-0.5 font-medium capitalize text-foreground">
                {organization?.role || "admin"}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Status</dt>
              <dd className="mt-0.5">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-success/15 px-2.5 py-0.5 text-xs font-medium capitalize text-success">
                  <span className="h-1.5 w-1.5 rounded-full bg-success" />
                  {organization?.status || "active"}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Signed in as</dt>
              <dd className="mt-0.5 font-medium text-foreground">{user?.email || "—"}</dd>
            </div>
          </dl>
          <button
            type="button"
            onClick={() => navigate("/business/settings")}
            className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            Manage company settings
            <Icon name="ArrowRight" size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}
